/* eslint-disable relay/unused-fields */
import type { FunctionComponent } from "react";
import { graphql, usePaginationFragment } from "react-relay";

import { DefaultPageSize } from "scmp-app/components/search/consts";
import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";
import type { articleSearchResultListQuery$key } from "scmp-app/queries/__generated__/articleSearchResultListQuery.graphql";
import type { searchArticleResultPaginationQuery } from "scmp-app/queries/__generated__/searchArticleResultPaginationQuery.graphql";

import { Item, List, StyledLoading } from "./styles";

export type Props = {
  reference: articleSearchResultListQuery$key;
};

export const ArticleSearchResultList: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const {
    data,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<searchArticleResultPaginationQuery, articleSearchResultListQuery$key>(
    graphql`
      fragment articleSearchResultListQuery on Query
      @refetchable(queryName: "searchArticleResultPaginationQuery") {
        articleSearch(
          filter: {
            query: $query
            sectionIds: $sectionIds
            articleTypes: $articleTypeIds
            paywallTypeIds: $paywallTypeIds
            contentTypes: $contentTypes
            startDate: $startDate
            endDate: $endDate
          }
          orderBy: $orderBy
          first: $first
          after: $after
        ) @connection(key: "articleSearchResultListQuery__articleSearch") {
          edges {
            node {
              id
              score
              content {
                ...searchContentItemContent
              }
            }
          }
        }
      }
    `,
    reference_,
  );

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(DefaultPageSize),
  });

  return data.articleSearch?.edges && data.articleSearch?.edges?.length > 0 ? (
    <>
      <List>
        {data.articleSearch?.edges?.map(edge =>
          edge.node.content ? <Item key={edge.node.id} reference={edge.node.content} /> : null,
        )}
      </List>
      {hasNextPage && (
        <StyledLoading
          ref={element => infiniteScrollTriggerReference(isLoading ? null : element)}
        />
      )}
    </>
  ) : null;
};

ArticleSearchResultList.displayName = "ArticleSearchResultList";
